import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CustomerRegistration, CustomerLogin, GetGuestAccounts, GeocodePostcode } from "../../model/IndexModel";
import ForgotPassword from "./ForgotPassword";
import MobileNavSidebar from "../../components/MobileNavSidebar";
import Cookies from "js-cookie";
// import "./Header.css";

const Header = ({ userSession }) => {
  const [customerInfo, setCustomerInfo] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [megSuccess, setMsgSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const loginModalRef = React.useRef(null);
  const signupModalRef = React.useRef(null);

  // Toggle Modals

  const [modalId, setModelId] = useState();
  const [SignupModelClass, setSignupModelClass] = useState(false);
  const [SigninModelClass, setSigninModelClass] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  // Signout
  const Signout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("customerEmail");
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("customerEmail");
    setTimeout(() => {
      navigate(0);
    }, 200);
    navigate("/", { replace: true });
  };

  // Signup Form
  const [formData, setFormData] = useState({
    name: "",
    phoneNum: "",
    email: "",
    password: "",
    address_line1: "",
    post_code: "",
  });

  const [formErrors, setFormErrors] = useState({
    name: "",
    phoneNum: "",
    email: "",
    password: "",
    address_line1: "",
    post_code: "",
  });

  // City/State/Country are derived from the postcode via Google Geocoding — not user-entered.
  const [geocodedAddress, setGeocodedAddress] = useState(null); // { city, state, country, post_code }
  const [geocoding, setGeocoding] = useState(false);

  const lookupPostcode = async (postcode) => {
    if (!postcode) return;
    setGeocoding(true);
    setGeocodedAddress(null);
    try {
      const result = await GeocodePostcode(postcode);
      if (result?.error) {
        setFormErrors((prevErrors) => ({ ...prevErrors, post_code: result.error }));
        return;
      }
      setGeocodedAddress(result);
    } finally {
      setGeocoding(false);
    }
  };

  const handlePostcodeBlur = (e) => {
    const postcode = e.target.value.trim();
    if (postcode) {
      lookupPostcode(postcode);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "", // Clear error message for the field being updated
    }));
    if (name === "post_code") {
      // Postcode changed since the last lookup — the resolved City/State/Country no longer apply.
      setGeocodedAddress(null);
    }
  };

  const handlePhoneNumberChange = (value) => {
    const maxDigits = 10;

    if (typeof value !== 'string') {
      value = String(value || ''); // convert null/undefined to ''
    }

    const sanitizedValue = value.replace(/\D/g, ''); // remove non-digits

    if (sanitizedValue.length <= maxDigits) {
      setFormData((prevData) => ({
        ...prevData,
        phoneNum: sanitizedValue,
      }));
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        phoneNum: "", // Clear error message for phone number
      }));
    }
  };

  const ukPostcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

  const SignUpForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { name, phoneNum, email, password, address_line1, post_code } = formData;

    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

    try {
      const errors = {
        name: !name ? "Name is required" : "",
        phoneNum: !phoneNum
          ? "Phone number is required"
          : !/^\d{10}$/.test(phoneNum)
            ? "Phone number must be exactly 10 digits."
            : "",
        email: !email
          ? "Email is required"
          : !/^\S+@\S+\.\S+$/.test(email)
            ? "Invalid email format."
            : "",
        password: !password
          ? "Password is required"
          : !passwordRegex.test(password)
            ? "Password must be at least 8 characters, include 1 uppercase letter, 1 number, and 1 special character."
            : "",
        address_line1: !address_line1 ? "Address Line 1 is required" : "",
        post_code: !post_code
          ? "Postcode is required"
          : !ukPostcodeRegex.test(post_code)
            ? "Enter a valid UK postcode."
            : "",
      };

      setFormErrors(errors);

      if (Object.values(errors).some((error) => error)) {
        setLoading(false);
        return;
      }

      // Make sure we have City/State/Country resolved for the postcode currently in the field
      // (covers the case where the user never blurred the field, or the earlier lookup failed).
      let resolvedAddress = geocodedAddress;
      if (!resolvedAddress || resolvedAddress.post_code?.replace(/\s/g, '').toUpperCase() !== post_code.replace(/\s/g, '').toUpperCase()) {
        setGeocoding(true);
        const result = await GeocodePostcode(post_code);
        setGeocoding(false);
        if (result?.error) {
          setFormErrors((prevErrors) => ({ ...prevErrors, post_code: result.error }));
          setLoading(false);
          return;
        }
        resolvedAddress = result;
        setGeocodedAddress(result);
      }

      const data = await CustomerRegistration(
        name,
        `+44${phoneNum}`,
        email,
        password,
        address_line1,
        resolvedAddress.city,
        resolvedAddress.state,
        resolvedAddress.post_code,
        resolvedAddress.country
      );
      if (data && !data.error) {
        setCustomerInfo(data || {});
        setFormData({ name: "", phoneNum: "", email: "", password: "", address_line1: "", post_code: "" });
        setFormErrors({ name: "", phoneNum: "", email: "", password: "", address_line1: "", post_code: "" });
        setGeocodedAddress(null);
        // Close signup modal, open login modal via Bootstrap API
        const signupBsModal = window.bootstrap?.Modal?.getInstance(signupModalRef.current);
        if (signupBsModal) signupBsModal.hide();
        const loginBsModal = window.bootstrap?.Modal?.getInstance(loginModalRef.current)
          || (loginModalRef.current && window.bootstrap ? new window.bootstrap.Modal(loginModalRef.current) : null);
        if (loginBsModal) loginBsModal.show();
      } else {
        throw new Error(data.error || "Registration failed");
      }
    } catch (err) {
      setMsgError(err.message);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (customerInfo && customerInfo?.code) {
      setMsgSuccess(customerInfo.msg || "");
      navigate(location.pathname + location.search);
    }
  }, [customerInfo, navigate, location.pathname, location.search]);

  useEffect(() => {
    const modelElement = loginModalRef.current;
    if (!modelElement) return;
    const handleModalHidden = () => {
      setSigninData({ email: "", password: "" });
      setFormErrors({ email: "", password: "" });
      setLoginError("");
    };
    modelElement.addEventListener('hidden.bs.modal', handleModalHidden);
    return () => {
      modelElement.removeEventListener('hidden.bs.modal', handleModalHidden);
    };
  }, []);

  // Cross-tab session sync: respond to other tabs requesting the session
  useEffect(() => {
    const SESSION_REQUEST_KEY = "__session_request__";
    const SESSION_RESPONSE_KEY = "__session_response__";

    const onStorage = (e) => {
      if (e.key === SESSION_REQUEST_KEY) {
        const userData = JSON.parse(sessionStorage.getItem("user"));
        if (userData && userData.customer_token) {
          localStorage.setItem(SESSION_RESPONSE_KEY, JSON.stringify(userData));
          // Clean up the response key shortly after so it doesn't linger
          setTimeout(() => localStorage.removeItem(SESSION_RESPONSE_KEY), 500);
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Auto-open login modal when redirected from Book Now without being logged in
  useEffect(() => {
    if (location.state?.openLogin && loginModalRef.current && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(loginModalRef.current)
        || new window.bootstrap.Modal(loginModalRef.current);
      modal.show();
      // Clear the state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const modelElement = signupModalRef.current;
    if (!modelElement) return;
    const handleModalHidden = () => {
      setFormData({ name: "", phoneNum: "", email: "", password: "", address_line1: "", post_code: "" });
      setFormErrors({ name: "", phoneNum: "", email: "", password: "", address_line1: "", post_code: "" });
      setGeocodedAddress(null);
      setMsgError("");
    };
    modelElement.addEventListener('hidden.bs.modal', handleModalHidden);
    return () => {
      modelElement.removeEventListener('hidden.bs.modal', handleModalHidden);
    };
  }, []);

  // Signin Form
  const [SigninData, setSigninData] = useState({
    email: "",
    password: "",
  });

  const handleChangeSignin = (e) => {
    const { name, value } = e.target;
    setSigninData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "", // Clear error message for the field being updated
    }));
  };

  const SigninForm = async (e) => {
    e.preventDefault();
    setMsgError("");
    const { email, password } = SigninData;

    const errors = {
      email: !email ? "Email is required." : "",
      password: !password ? "Password is required." : "",
    };
    const formatAs24Hour=(isoDate)=> {
    const date = new Date(isoDate);

    if (
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0
    ) {
        const previousDay = new Date(date);
        previousDay.setUTCDate(previousDay.getUTCDate() - 1);

        const year = previousDay.getUTCFullYear();
        const month = String(previousDay.getUTCMonth() + 1).padStart(2, '0');
        const day = String(previousDay.getUTCDate()).padStart(2, '0');

        return `${year}-${month}-${day}T24:00:00.000Z`;
    }

    return isoDate;
}
    setFormErrors(errors);

    if (Object.values(errors).some((error) => error)) {
      return;
    }

    try {
      setLoading(true);
      const data = await CustomerLogin(email, password);

      if (data?.record) {
        const { customer_token, fullname, email, code, allow_pay_direct } = data.record;
        const customerData = { customer_token, fullname, email, allow_pay_direct: allow_pay_direct === true };

        sessionStorage.setItem("user", JSON.stringify(customerData));

        const consentCookie = Cookies.get("cookie_consent");
        let consent = null;

        if (consentCookie) {
          try { consent = JSON.parse(consentCookie).consent; } catch { }
        }

        if (consent === "accepted") {
          localStorage.setItem("user", JSON.stringify(customerData));
          Cookies.set("user_data", JSON.stringify(customerData));
        } else {
          localStorage.removeItem("user");
        }

        if (code === 200) {
          // Force-remove modal backdrop synchronously before navigating
          if (loginModalRef.current) {
            loginModalRef.current.classList.remove('show');
            loginModalRef.current.style.display = 'none';
          }
          document.body.classList.remove('modal-open');
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
          document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());

          const pendingBookingRaw = sessionStorage.getItem('FacilitiesDetailsFormData');
          const intendedPath = location.state?.from;
          if (pendingBookingRaw) {
            const fd = JSON.parse(pendingBookingRaw);
           console.log(fd,"/checkout")
            navigate(
              `/checkout?utoken=${customerData.customer_token}&ftoken=${fd.token}&cindate=${fd.checkinDisplay || fd.checkin}&coutdate=${formatAs24Hour(fd.checkout)}`,
              {
                replace: true,
                state: {
                  customer_token: customerData.customer_token,
                  hosttoken: fd.hosttoken,
                  facilities_token: fd.token,
                  checkin: fd.checkin,
                  checkout: fd.checkout,
                  total_guest: fd.total_guest,
                  rent: fd.rent,
                  booking_fee: fd.booking_fee,
                  total: fd.total,
                  OnlyPerHrAmount: fd.OnlyPerHrAmount,
                  purpose: fd.purpose,
                  isRecurring: fd.isRecurring,
                  recurringFrequency: fd.recurringFrequency,
                  recurringOccurrences: fd.recurringOccurrences,
                  bookingReason: fd.bookingReason,
                  selectedExtras: fd.selectedExtras,
                  extrasSubtotal: fd.extrasSubtotal,
                }
              }
            );
          } else if (intendedPath && intendedPath !== '/') {
            navigate(intendedPath, { replace: true });
          } else {
            navigate(location.pathname + location.search, { replace: true });
            window.location.reload();
          }
        } else {
          setMsgError("Invalid login credentials.");
        }
      } else {
        setLoginError("Invalid login credentials.");
      }
    } catch (err) {
      console.error(err);
      setLoginError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // Get User Details
  const GetGuestdetails = async () => {
    try {
      setLoading(true);
      const data = await GetGuestAccounts(userSession.customer_token);
      if (data.record) {
        setCustomerData(data.record);
      } else {
        console.error("No data received from the API.");
      }
    } catch (error) {
      console.error("Error fetching facility data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userSession.customer_token) {
      GetGuestdetails();
    }
  }, [userSession.customer_token]);


  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const handleSigninClose = () => {
    const modal = window.bootstrap?.Modal?.getInstance(loginModalRef.current);
    if (modal) {
      modal.hide();
    } else {
      // Fallback: manually clear backdrop if Bootstrap instance is unavailable
      if (loginModalRef.current) {
        loginModalRef.current.classList.remove('show');
        loginModalRef.current.style.display = 'none';
      }
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) backdrop.remove();
    }
  };

  return (
    <>
    <header>
      <nav className="navbar navbar-light">
        <div className="container-fluid">
          <Link to="/" className="navbar-brand"><img className="image-logo" src={`${process.env.PUBLIC_URL}/img/logo.png`} alt="Logo" /></Link>

          {/* Desktop nav — logged-out only, hidden on mobile */}
          {!userSession?.customer_token && (
            <div className="d-none d-lg-flex align-items-center" style={{marginLeft: 'auto', gap: '4px'}}>
              <a
                href={`${process.env.REACT_APP_API_URL}/login`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#888', padding: '10px 15px', textDecoration: 'none', fontSize: '1rem', fontWeight: 'bold', whiteSpace: 'nowrap', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#eb2a25'}
                onMouseLeave={e => e.currentTarget.style.color = '#888'}
              >
                Host log in
              </a>
              <a
                href="#"
                data-bs-toggle="modal"
                data-bs-target="#signinModel"
                style={{ color: '#000000', padding: '10px 15px', textDecoration: 'none', fontSize: '1rem', fontWeight: 'bold', whiteSpace: 'nowrap', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#eb2a25'}
                onMouseLeave={e => e.currentTarget.style.color = '#000000'}
              >
                Log in
              </a>
              <a
                href={process.env.REACT_APP_API_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ backgroundColor: '#eb2a25', color: '#ffffff', padding: '8px 20px', borderRadius: '50px', textDecoration: 'none', fontSize: '1rem', fontWeight: 'bold', whiteSpace: 'nowrap', transition: 'background-color 0.2s', marginLeft: '8px', display: 'inline-block' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#c0211d'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#eb2a25'}
              >
                List your hall
              </a>
            </div>
          )}

          {/* Profile — logged-in only, always visible (all screen sizes) */}
          {userSession?.customer_token && (
            <div className="d-flex align-items-center" style={{marginLeft: 'auto', gap: '8px'}}>
              <span className="d-none d-lg-inline" style={{ color: '#eb2a25', fontWeight: 'bold' }}>{userSession.fullname}</span>
              <div className="dropdown">
                <button
                  className="btn-dropdown border-0 bg-transparent dropdown-toggle"
                  type="button"
                  id="dropdownMenuButton1"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {customerData[0]?.profil_image ? (
                    <img src={customerData[0].profil_image} alt="Profile" style={{ width: 30, height: 30, borderRadius: 50, objectFit: 'cover' }} />
                  ) : (
                    <img src={`${process.env.PUBLIC_URL}/img/div.fs7xov7.svg`} alt="Account" />
                  )}
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuButton1">
                  <li style={{ paddingLeft: '15px', color: '#eb2a25' }}>{userSession.fullname}</li>
                  <li><Link to="/dashboard" className="dropdown-item">Dashboard</Link></li>
                  <li><Link to="/profile" className="dropdown-item">Profile</Link></li>
                  <li><Link to="/contact-us" className="dropdown-item">Support</Link></li>
                  <li><button className="dropdown-item" onClick={Signout}>Log out</button></li>
                </ul>
              </div>
            </div>
          )}

          {/* Mobile CTA + hamburger — logged-out only */}
          {!userSession?.customer_token && (
            <div className="d-flex d-lg-none align-items-center" style={{gap: '8px'}}>
              <a
                href={process.env.REACT_APP_API_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-header-cta"
              >
                List your hall
              </a>
              <button
                className="navbar-toggler"
                type="button"
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open navigation menu"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>

    {/* Sidebar — logged-out only */}
    {!userSession?.customer_token && (
      <MobileNavSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)}>
        <a href={`${process.env.REACT_APP_API_URL}/login`} target="_blank" rel="noopener noreferrer" onClick={() => setIsSidebarOpen(false)}>Host log in</a>
        <a href="#" data-bs-toggle="modal" data-bs-target="#signinModel" onClick={() => setIsSidebarOpen(false)}>Log in</a>
      </MobileNavSidebar>
    )}

      {/* Signin Model */}
      <div ref={loginModalRef} className="modal fade" id="signinModel" tabIndex="-1" aria-labelledby="exampleModalLabel">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">Login or Register</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleSigninClose} // Close Signin modal
              ></button>
            </div>
            <form onSubmit={SigninForm}>
              <div className="modal-body pb-0">
                <label className="col-form-label pt-0">
                  Welcome to <b className="title-color">Simply Halls</b>
                </label>
                <div className="mb-3">
                  <label>Email *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Email"
                    name="email"
                    value={SigninData.email}
                    onChange={handleChangeSignin}
                  />
                  {formErrors.email && <p className="text-danger">{formErrors.email}</p>}
                </div>
                <div className="mb-3">
                  <label>Password *</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showLoginPassword && SigninData.password ? "text" : "password"}
                      className="form-control"
                      placeholder="Password"
                      name="password"
                      value={SigninData.password}
                      onChange={handleChangeSignin}
                      style={{ paddingRight: "38px" }}
                    />
                    {SigninData.password && (
                      <span
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", background: "transparent", border: "none", color: "#aaa", display: "flex", alignItems: "center" }}
                      >
                        <i className={showLoginPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                      </span>
                    )}
                  </div>
                  {formErrors.password && <p className="text-danger">{formErrors.password}</p>}
                </div>
                {loginError && <p className="text-center text-danger">{loginError}</p>}
                {megSuccess && <p className="resMsg">{megSuccess}</p>}
              </div>
              <div className="modal-footer border-top-0 pt-1">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    "Login"
                  )}
                </button>
                <div className="title-signup hide-details">
                  Don’t have an Account? <a
                    href="#"
                    data-bs-toggle="modal"
                    data-bs-target="#SignUp"
                    data-bs-dismiss="modal"
                    onClick={() => {
                      setMsgError("");
                      setFormErrors({});
                    }}
                  >Register</a>
                </div>
              </div>
              <div className="text-center mb-3 title-signup hide-details">
                <a
                  href="#"
                  data-bs-toggle="modal"
                  data-bs-target="#forgotPasswordModal"
                  data-bs-dismiss="modal"
                >
                  Forgot Password
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Singup Model */}
      <div ref={signupModalRef} className="modal fade" id="SignUp" tabIndex="-1" aria-labelledby="SignUpLabel">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="SignUpLabel">Register here</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={SignUpForm}>
              <div className="modal-body pb-0">
                <label>Name *</label>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  {formErrors.name && <p className="text-danger">{formErrors.name}</p>}
                </div>
                <div className="mb-3">
                  <label>Phone No. *</label>
                  <div className="phone-container">
                    <div className="phone-prefix">
                      <img
                        src="https://flagcdn.com/gb.svg"
                        alt="GB Flag"
                        className="phone-flag"
                      />
                      <span>+44</span>
                    </div>
                    <input
                      type="text"
                      name="phoneNum"
                      placeholder="Phone No"
                      value={formData.phoneNum}
                      onChange={(e) => handlePhoneNumberChange(e.target.value)}
                      maxLength={10}
                      className="form-control phone-input"
                      autoComplete="off"
                    />
                  </div>
                  {formErrors.phoneNum && (
                    <p className="text-danger mt-1">{formErrors.phoneNum}</p>
                  )}
                </div>
                <div className="mb-3">
                  <label>Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  {formErrors.email && <p className="text-danger">{formErrors.email}</p>}
                </div>
                <div className="mb-3">
                  <label>Address Line 1 *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Address Line 1"
                    name="address_line1"
                    value={formData.address_line1}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                  {formErrors.address_line1 && <p className="text-danger">{formErrors.address_line1}</p>}
                </div>
                <div className="mb-3">
                  <label>Postcode *</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Postcode"
                    name="post_code"
                    value={formData.post_code}
                    onChange={handleChange}
                    onBlur={handlePostcodeBlur}
                    autoComplete="off"
                  />
                  {geocoding && <p className="text-muted mt-1">Looking up address…</p>}
                  {!geocoding && geocodedAddress && !formErrors.post_code && (
                    <p className="text-success mt-1">
                      {geocodedAddress.city}, {geocodedAddress.state}, {geocodedAddress.country}
                    </p>
                  )}
                  {formErrors.post_code && <p className="text-danger">{formErrors.post_code}</p>}
                </div>
                <div className="mb-3">
                  <label>Password *</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showSignupPassword && formData.password ? "text" : "password"}
                      className="form-control"
                      placeholder="Password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="off"
                      style={{ paddingRight: "38px" }}
                    />
                    {formData.password && (
                      <span
                        onClick={() => setShowSignupPassword(!showSignupPassword)}
                        style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", background: "transparent", border: "none", color: "#aaa", display: "flex", alignItems: "center" }}
                      >
                        <i className={showSignupPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                      </span>
                    )}
                  </div>
                  {formErrors.password && <p className="text-danger">{formErrors.password}</p>}
                </div>
                {msgError && <p className="text-center text-danger">{msgError}</p>}
              </div>
              <div className="modal-footer border-top-0 pt-1">
                {modalId > 0 ? (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    data-bs-toggle="modal"
                    data-bs-target={modalId || ''}
                    data-bs-dismiss={modalId ? 'modal' : ''}
                  >
                    {loading ? 'Submitting...' : 'Submit'}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Submit'}
                  </button>
                )}

                <div className="title-signup">
                  Already have an account?&nbsp;
                  <a href="#"
                    data-bs-toggle="modal"
                    data-bs-target="#signinModel"
                    data-bs-dismiss="modal"
                    onClick={() => {
                      setMsgError("");
                      setFormErrors({});
                    }}>
                    Login
                  </a>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ForgotPassword />
    </>

  );
};


export default Header;
